import React, { useState, useEffect, useMemo } from "react";
import { api } from "../lib/api";
import { txnKeys, computeWithinDup, computeCrossKeys, dupRowLabel } from "../lib/txnDuplicates";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Save, Pencil, Copy, AlertTriangle } from "lucide-react";

const TEXT_FIELDS = [
  { key: "issuing_authority", label: "Issuing authority" },
  { key: "document_type", label: "Document type" },
  { key: "account_id", label: "Account ID", mono: true },
  { key: "invoice_number", label: "Invoice #", mono: true },
  { key: "license_plate", label: "License plate", mono: true },
  { key: "plate_state", label: "Plate state" },
  { key: "owner_name", label: "Owner name" },
  { key: "currency", label: "Currency" },
  { key: "bill_date", label: "Bill date", placeholder: "YYYY-MM-DD" },
  { key: "due_date", label: "Due date", placeholder: "YYYY-MM-DD" },
];

const NUMERIC_FIELDS = [
  { key: "toll_amount", label: "Toll amount" },
  { key: "processing_fee", label: "Processing fee" },
  { key: "late_fees", label: "Late fees" },
  { key: "previous_balance", label: "Previous balance" },
  { key: "total_amount_due", label: "Total due" },
];

const EMPTY_TXN = { date: "", time: "", location: "", transaction_number: "", amount: "" };

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

// Stable per-row identity for React keys on editable transaction rows. Using a
// position index as the key would reuse inputs incorrectly when rows are added
// or removed; a stable key preserves input focus and state.
let _txnKeySeq = 0;
const newTxnKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `txn-${Date.now()}-${_txnKeySeq++}`);

export const BillEditDialog = ({ bill, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({});
  const [txns, setTxns] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!bill || !open) return;
    const f = {};
    [...TEXT_FIELDS, ...NUMERIC_FIELDS].forEach(({ key }) => (f[key] = toStr(bill[key])));
    f.notes = toStr(bill.notes);
    setForm(f);
    setTxns((bill.transactions || []).map((t) => ({
      _key: newTxnKey(),
      date: toStr(t.date), time: toStr(t.time), location: toStr(t.location),
      transaction_number: toStr(t.transaction_number), amount: toStr(t.amount),
    })));
  }, [bill, open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setTxn = (i, k, v) => setTxns((p) => p.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const addTxn = () => setTxns((p) => [...p, { ...EMPTY_TXN, _key: newTxnKey() }]);
  const removeTxn = (i) => setTxns((p) => p.filter((_, idx) => idx !== i));

  // Live within-bill duplicate detection (updates as the user edits/removes rows).
  const withinDup = useMemo(() => computeWithinDup(txns), [txns]);

  // Cross-bill duplicate signatures (content-based, from the server review) — robust to row edits.
  const crossKeys = useMemo(() => computeCrossKeys(bill), [bill]);

  const isCross = (t) => crossKeys.size > 0 && txnKeys(t).some((k) => crossKeys.has(k));
  const withinCount = Object.keys(withinDup).length;
  const crossCount = useMemo(
    () => txns.filter((t) => crossKeys.size > 0 && txnKeys(t).some((k) => crossKeys.has(k))).length,
    [txns, crossKeys]
  );

  const save = async () => {
    setSaving(true);
    try {
      // Strip the client-only `_key` (React row identity) before persisting.
      const cleanTxns = txns.map(({ _key, ...t }) => t);
      const payload = { ...form, transactions: cleanTxns };
      const res = await api.updateBill(bill.id, payload);
      toast.success("Changes saved");
      onSaved && onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  if (!bill) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bill-edit-dialog" className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Edit bill
          </DialogTitle>
          <DialogDescription>
            Correct any mis-read fields. Edited fields are marked as verified and won't be flagged for review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TEXT_FIELDS.map(({ key, label, mono, placeholder }) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`edit-${key}`} className="text-xs">{label}</Label>
                <Input
                  id={`edit-${key}`}
                  data-testid={`bill-edit-${key}`}
                  className={mono ? "font-mono" : ""}
                  placeholder={placeholder}
                  value={form[key] ?? ""}
                  onChange={(e) => set(key, e.target.value)}
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {NUMERIC_FIELDS.map(({ key, label }) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`edit-${key}`} className="text-xs">{label}</Label>
                <Input
                  id={`edit-${key}`}
                  data-testid={`bill-edit-${key}`}
                  inputMode="decimal"
                  className="tabular-nums"
                  value={form[key] ?? ""}
                  onChange={(e) => set(key, e.target.value)}
                />
              </div>
            ))}
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-notes" className="text-xs">Notes</Label>
            <Textarea id="edit-notes" data-testid="bill-edit-notes" rows={2}
              value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Toll transactions</Label>
              <Button type="button" size="sm" variant="outline" className="gap-1 h-7" data-testid="bill-edit-add-txn" onClick={addTxn}>
                <Plus className="h-3.5 w-3.5" /> Add row
              </Button>
            </div>
            {(withinCount > 0 || crossCount > 0) && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2" data-testid="dup-txn-banner">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div className="space-y-0.5">
                  {withinCount > 0 && (
                    <div>{withinCount} duplicate transaction{withinCount > 1 ? "s" : ""} within this bill — remove the extra row{withinCount > 1 ? "s" : ""} and save.</div>
                  )}
                  {crossCount > 0 && (
                    <div>{crossCount} transaction{crossCount > 1 ? "s" : ""} also appear in other bills.</div>
                  )}
                </div>
              </div>
            )}
            {txns.length === 0 ? (
              <p className="text-xs text-muted-foreground">No transactions. Add a row if needed.</p>
            ) : (
              <div className="space-y-2">
                {txns.map((t, i) => {
                  const dupOf = withinDup[i];
                  const within = dupOf !== undefined;
                  const cross = isCross(t);
                  const flagged = within || cross;
                  const label = dupRowLabel({ within, cross, dupOf });
                  return (
                    <div
                      key={t._key}
                      className={`rounded-md ${flagged ? "ring-1 ring-amber-300 bg-amber-50/60 p-1.5" : ""}`}
                      data-testid="bill-edit-txn-row"
                    >
                      {flagged && (
                        <div className="flex items-center gap-1.5 mb-1 px-1" data-testid="bill-edit-txn-dup-flag">
                          <Copy className="h-3 w-3 text-amber-600 shrink-0" />
                          <span className="text-[11px] font-medium text-amber-700">{label}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <Input className="col-span-3" placeholder="Date" value={t.date} onChange={(e) => setTxn(i, "date", e.target.value)} />
                        <Input className="col-span-2" placeholder="Time" value={t.time} onChange={(e) => setTxn(i, "time", e.target.value)} />
                        <Input className="col-span-4" placeholder="Location" value={t.location} onChange={(e) => setTxn(i, "location", e.target.value)} />
                        <Input className="col-span-2 tabular-nums" placeholder="Amount" inputMode="decimal" value={t.amount} onChange={(e) => setTxn(i, "amount", e.target.value)} />
                        <Button type="button" size="icon" variant="ghost" className="col-span-1 h-8 w-8" onClick={() => removeTxn(i)} aria-label="Remove transaction" data-testid="bill-edit-remove-txn">
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="gap-2" data-testid="bill-edit-save-button">
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
